import { z } from "zod";



// type PVAreaPayload = {
//   [key: number]: {
//     area_pv: number;
//     name_pv: string;
//     floor_count_building: number | null;
//     type_pv: string;
//     percentage_of_area_covered: number;
//     orientation_pv: string;
//     orientation_pv_angle: number;
//     angle_pv: number;
//     uuid: string;
//     polygon_data_pv: string;
//   };
//   access_previlege: string;
//   model_name: string;
//   uuid: string;
// };


const numberOrNaN = z.preprocess(
  (v) => (typeof v === "number" ? v : Number(v)),
  z.union([z.number(), z.nan()])
);

// number | NaN | null  (preserves null)
const numberOrNaNOrNull = z.preprocess(
  (v) => (v === null ? null : typeof v === "number" ? v : Number(v)),
  z.union([z.number(), z.nan(), z.null()])
);


export const PVAreaItemSchema = z.object({
  area_pv: numberOrNaN.optional(),
  name_pv: z.coerce.string().optional(),
  floor_count_building: numberOrNaNOrNull.optional(),
  type_pv: z.coerce.string().optional(),
  percentage_of_area_covered: numberOrNaN.optional(),
  orientation_pv: z.coerce.string().optional(),
  orientation_pv_angle: numberOrNaN.optional(),
  angle_pv: numberOrNaN.optional(),
  uuid: z.coerce.string().optional(),
  polygon_data_pv: z.coerce.string().optional(), // JSON string like '[[lon,lat], ...]'
});

const PVAreaPayloadObjectSchema = z
  .object({
    access_previlege: z.coerce.string(),
    model_name: z.coerce.string(),
    uuid: z.coerce.string(),
  })
  .catchall(PVAreaItemSchema)
  .superRefine((obj, ctx) => {
    for (const k of Object.keys(obj)) {
      if (k === "access_previlege" || k === "model_name" || k === "uuid") continue;
      if (!/^\d+$/.test(k)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [k],
          message: "Key must be an integer-like string (e.g., '0', '1', '42')",
        });
      }
    }
  });

export const PVAreaPayloadInputSchema = z.preprocess((v: unknown) => {
  if (Array.isArray(v)) {
    // pull extra props from the array object and re-key items by index
    const anyV = v as any;
    const base: Record<string, unknown> = {
      access_previlege: anyV.access_previlege,
      model_name: anyV.model_name,
      uuid: anyV.uuid,
    };
    v.forEach((item, i) => {
      base[String(i)] = item;
    });
    return base;
  }
  return v;
}, PVAreaPayloadObjectSchema);

export type PVAreaPayload = z.infer<typeof PVAreaPayloadInputSchema>;

// type ElectricityPayload = {
//     access_previlege:string,
//     model_name:string,
//     uuid:string,
//     total_consumption_el:number,
//     av_price_el:number,
// }


export const ElectricityPayloadSchema = z.object({
  access_previlege: z.coerce.string(),
  model_name: z.coerce.string(),
  uuid: z.coerce.string(),
  total_consumption_el: z.coerce.number().optional(),
  av_price_el: z.coerce.number().optional(),
});

export type ElectricityPayload = z.infer<typeof ElectricityPayloadSchema>;


// type LoadProfilePayload = {
//     access_previlege:string,
//     model_name:string,
//     uuid:string,
//     daily_profile:string,
//     monthly_profile:string,
//     type_of_loadprofile:string
// }



export const LoadProfilePayloadSchema = z.object({
  access_previlege: z.coerce.string(),
  model_name: z.coerce.string(),
  uuid: z.coerce.string(),
  daily_profile: z.coerce.string().optional(),
  monthly_profile: z.coerce.string().optional(),
  type_of_loadprofile: z.coerce.string().optional(),
});

export type LoadProfilePayload = z.infer<typeof LoadProfilePayloadSchema>;

// type FurtherInformationPayload = {
//     access_previlege:string,
//     model_name:string,
//     uuid:string,
//     area_of_building?:number,
//     count_fast_ev?:number,
//     count_fast_public_ev?:number,
//     count_normal_ev?:number,
//     count_normal_public_ev?:number,
// }


export const FurtherInformationPayloadSchema = z.object({
  // required
  access_previlege: z.coerce.string(),
  model_name: z.coerce.string(),
  uuid: z.coerce.string(),

  // optional
  area_of_building: z.coerce.number().optional(),
  count_fast_ev: z.coerce.number().optional(),
  count_fast_public_ev: z.coerce.number().optional(),
  count_normal_ev: z.coerce.number().optional(),
  count_normal_public_ev: z.coerce.number().optional(),
});

export type FurtherInformationPayload = z.infer<typeof FurtherInformationPayloadSchema>;

// type HeatInformationPayload = {
//     access_previlege:string,
//     model_name:string,
//     uuid:string,
//     construction_year_heat?:number,
//     cost_unit_heat?:number,
//     data_uploaded?:string,
//     flow_temperature?:number,
//     heating_limit_temperature?:number,
//     id_heat?:number,
//     isGenerateProcessHeat?:boolean,
//     isUploadFile?:boolean,
//     source_heat?:string,
//     total_consumption_heat?:number,
//     unit_heat_amount?:string,
//     unit_heat_cost?:string,
//     year_consumption_heat?:number,
// }


export const HeatInformationPayloadSchema = z.object({
  // required
  access_previlege: z.coerce.string(),
  model_name: z.coerce.string(),
  uuid: z.coerce.string(),

  // optional
  construction_year_heat: z.coerce.number().optional(),
  cost_unit_heat: z.coerce.number().optional(),
  data_uploaded: z.coerce.string().optional(),
  flow_temperature: z.coerce.number().optional(),
  heating_limit_temperature: z.coerce.number().optional(),
  id_heat: z.coerce.number().optional(),
  isGenerateProcessHeat: z.coerce.boolean().optional(),
  isUploadFile: z.coerce.boolean().optional(),
  source_heat: z.coerce.string().optional(),
  total_consumption_heat: z.coerce.number().optional(),
  unit_heat_amount: z.coerce.string().optional(),
  unit_heat_cost: z.coerce.string().optional(),
  year_consumption_heat: z.coerce.number().optional(),
});

export type HeatInformationPayload = z.infer<typeof HeatInformationPayloadSchema>;


// type LocationInformation = {
//   access_previlege:string,
//   model_name:string,
//   uuid:string,
//   address:string,
//   city:string,
//   latitude:number,
//   longitude:number,
//   mapbox_id:string,
//   postcode:string,
//   street_name:string,
//   street_number:string,

// }

export const LocationInformationSchema = z.object({
  access_previlege: z.coerce.string(),
  model_name:       z.coerce.string(),
  uuid:             z.coerce.string(),
  address:          z.coerce.string().optional(),
  city:             z.coerce.string().optional(),
  latitude:         z.coerce.number().optional(),
  longitude:        z.coerce.number().optional(),
  mapbox_id:        z.coerce.string().optional(),
  postcode:         z.coerce.string().optional(),
  street_name:      z.coerce.string().optional(),
  street_number:    z.coerce.string().optional(),
});

export type LocationInformation = z.infer<typeof LocationInformationSchema>;